import React, { useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  Grid,
  Card,
  CardMedia,
  CardActions
} from '@mui/material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import NavBarAdm from './navbarAdm';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LinkIcon from '@mui/icons-material/Link';

// Helper function to format date from YYYY-MM-DD to DD.MM.YYYY
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}.${month}.${year}`;
};

const AddInfo = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    description: '',
    images: []
  });

  const [imageInput, setImageInput] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // Handle form field changes
  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  // Add image URL
  const addImageUrl = () => {
    if (imageInput.trim() && !formData.images.includes(imageInput.trim())) {
      setFormData({
        ...formData,
        images: [...formData.images, imageInput.trim()]
      });
      setImageInput('');
    }
  };

  // Remove image
  const removeImage = (indexToRemove) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, index) => index !== indexToRemove)
    });
  };

  // Handle image file upload and convert to Base64
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setMessage('Bitte wählen Sie eine Bilddatei aus');
      setMessageType('error');
      return;
    }

    // Check file size (limit to 3MB for original file)
    if (file.size > 3 * 1024 * 1024) {
      setMessage('Bild ist zu groß. Maximale Größe: 3MB');
      setMessageType('error');
      return;
    }

    setImageUploading(true);

    // Create canvas for image compression
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      let maxWidth, maxHeight, quality;

      // Determine compression settings based on file size
      if (file.size > 2 * 1024 * 1024) {
        maxWidth = 1200;
        maxHeight = 900;
        quality = 0.8;
      } else if (file.size > 1 * 1024 * 1024) {
        maxWidth = 1400;
        maxHeight = 1000;
        quality = 0.85;
      } else {
        maxWidth = 1600;
        maxHeight = 1200;
        quality = 0.9;
      }

      // Calculate aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      let base64String = canvas.toDataURL('image/jpeg', quality);
      let base64Size = Math.round((base64String.length * 3) / 4);

      // Additional compression if needed
      let attempts = 0;
      while (base64Size > 700 * 1024 && attempts < 3) {
        attempts++;
        quality = Math.max(quality - 0.1, 0.4);
        
        if (attempts > 1) {
          width = Math.round(width * 0.9);
          height = Math.round(height * 0.9);
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
        }
        
        base64String = canvas.toDataURL('image/jpeg', quality);
        base64Size = Math.round((base64String.length * 3) / 4);
      }
      
      if (base64Size > 700 * 1024) {
        setImageUploading(false);
        setMessage('Bild konnte nicht ausreichend komprimiert werden.');
        setMessageType('error');
        return;
      }
      
      setFormData({
        ...formData,
        images: [...formData.images, base64String]
      });
      
      setImageUploading(false);
      setMessage(`Bild erfolgreich hinzugefügt! (${Math.round(base64Size / 1024)}KB)`);
      setMessageType('success');
      
      event.target.value = '';
    };

    img.onerror = () => {
      setImageUploading(false);
      setMessage('Fehler beim Laden des Bildes');
      setMessageType('error');
    };

    img.src = URL.createObjectURL(file);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!formData.title.trim()) {
      setMessage('Titel ist erforderlich');
      setMessageType('error');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      setMessage('Start- und Enddatum sind erforderlich');
      setMessageType('error');
      return;
    }

    try {
      setLoading(true);
      
      const dataToSave = {
        title: formData.title.trim(),
        startDate: formatDateToDDMMYYYY(formData.startDate),
        endDate: formatDateToDDMMYYYY(formData.endDate),
        description: formData.description.trim(),
        images: formData.images,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Save to "FirmaInfos" collection
      await addDoc(collection(db, 'FirmaInfos'), dataToSave);
      
      setMessage('Firmen-Info erfolgreich hinzugefügt!');
      setMessageType('success');
      
      // Reset form after successful save
      setTimeout(() => {
        setFormData({
          title: '',
          startDate: '',
          endDate: '',
          description: '',
          images: []
        });
        setMessage('');
      }, 2000);
      
    } catch (error) {
      console.error('Error saving document: ', error);
      setMessage(`Fehler beim Speichern: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavBarAdm />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Neue Firmen-Info hinzufügen
          </Typography>
          
          <Divider sx={{ mb: 3 }} />

          {message && (
            <Alert severity={messageType} sx={{ mb: 3 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Title */}
            <TextField
              fullWidth
              label="Titel *"
              value={formData.title}
              onChange={handleInputChange('title')}
              margin="normal"
              required
            />

            {/* Date Range */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="Startdatum *"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange('startDate')}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ flex: 1 }}
              />
              <TextField
                label="Enddatum *"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange('endDate')}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Description */}
            <TextField
              fullWidth
              label="Beschreibung"
              value={formData.description}
              onChange={handleInputChange('description')}
              margin="normal"
              multiline
              rows={4}
            />

            <Divider sx={{ my: 3 }} />

            {/* Images Section */}
            <Typography variant="h6" gutterBottom>
              Bilder
            </Typography>

            {/* Add Image URL */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Bild-URL hinzufügen"
                value={imageInput}
                onChange={(e) => setImageInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImageUrl())}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={addImageUrl} startIcon={<LinkIcon />}>
                URL hinzufügen
              </Button>
            </Box>

            {/* Upload Image File */}
            <Box sx={{ mb: 3 }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<AddPhotoAlternateIcon />}
                disabled={imageUploading}
              >
                {imageUploading ? 'Wird hochgeladen...' : 'Bild hochladen'}
              </Button>
            </Box>

            {/* Display Images */}
            {formData.images.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {formData.images.map((img, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={img}
                        alt={`Bild ${index + 1}`}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardActions>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeImage(index)}
                          startIcon={<DeleteIcon />}
                        >
                          Entfernen
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Submit Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/adm')}
                disabled={loading}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Speichern...' : 'Hinzufügen'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default AddInfo;